/**
 * Application support context provider
 * Provides application-specific feature support flags to child components
 */

import { ReactNode, createContext, useContext, useMemo } from 'react';
import { useScratchpadServiceContext } from '../../components/machine/scratchpad/scratchpad-service-context-provider';
import { BeaconCuttingData } from '../../scratchpad/application/beacon/cutting/model/beacon-cutting-data';
import { BeaconPaintingData } from '../../scratchpad/application/beacon/painting/model/beacon-painting-data';
import { BeaconWeldingData } from '../../scratchpad/application/beacon/welding/model/beacon-welding-data';
import { logger } from '../../util/logger';

export enum ApplicationFeature {
  FINE_TUNE = 'fine_tune',
}

type FeatureSupportMap = Record<ApplicationFeature, boolean>;

// Mapping of application slugs to their feature support configurations
type ApplicationFeatureSupportMap = Record<string, FeatureSupportMap>;

/**
 * Feature support configuration map
 * Defines which features are supported by each application type
 */
const FEATURE_SUPPORT_MAP: ApplicationFeatureSupportMap = {
  [BeaconWeldingData.APP_SLUG]: {
    [ApplicationFeature.FINE_TUNE]: true,
  },
  [BeaconPaintingData.APP_SLUG]: {
    [ApplicationFeature.FINE_TUNE]: false,
  },
  [BeaconCuttingData.APP_SLUG]: {
    [ApplicationFeature.FINE_TUNE]: true,
  },
};

export interface ApplicationSupportContextValue {
  featureSupport: FeatureSupportMap;
}

export const ApplicationSupportContext = createContext<
  ApplicationSupportContextValue | undefined
>(undefined);

export interface ApplicationSupportProviderProps {
  children: ReactNode;
}

export const ApplicationSupportProvider = (
  props: ApplicationSupportProviderProps
): ReactNode => {
  const { children } = props;
  const scratchpadContext = useScratchpadServiceContext();

  const contextValue = useMemo<
    ApplicationSupportContextValue | undefined
  >(() => {
    if (!scratchpadContext) {
      return undefined;
    }

    const { scratchpad } = scratchpadContext;
    const appSlug = scratchpad?.app?.slug;

    if (!appSlug) {
      return undefined;
    }

    const featureSupport: FeatureSupportMap | undefined =
      FEATURE_SUPPORT_MAP[appSlug];

    // Determine application type from app slug
    if (featureSupport === undefined) {
      logger.warn(
        '[application-support] app slug does not have feature support mapping',
        { appSlug }
      );
      return undefined;
    }

    return {
      featureSupport,
    };
  }, [scratchpadContext]);

  return (
    <ApplicationSupportContext.Provider value={contextValue}>
      {children}
    </ApplicationSupportContext.Provider>
  );
};

/**
 * Hook to access the full application support context
 * @returns ApplicationSupportContextValue or undefined if context is not available
 */
export function useApplicationSupportContext():
  | ApplicationSupportContextValue
  | undefined {
  return useContext(ApplicationSupportContext);
}

export interface UseHasApplicationSupportOptions {
  feature: ApplicationFeature;
  defaultValue?: boolean;
}

/**
 * Hook to check if a feature is supported by the current application
 * @param options - feature name and optional default value
 * @returns true if feature is supported, false otherwise. Returns default value if context is unavailable.
 */
export function useHasApplicationSupport(
  options: UseHasApplicationSupportOptions
): boolean {
  const context = useApplicationSupportContext();
  const { feature, defaultValue = true } = options;

  if (!context) {
    return defaultValue;
  }

  return context.featureSupport[feature] ?? defaultValue;
}
